import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Check, X, Sparkles } from "lucide-react";
import {
  fetchFaceShapeCatalog,
  saveFaceShape,
} from "../lib/api";
import { track } from "../lib/analytics";

/**
 * FaceShapeCalibrator — modal picker that lets the user confirm or
 * override the face-shape the AR mirror detected. Writes with
 * `source: "user_set"` so the live classifier no longer overwrites.
 *
 * Theme: "mirror" (dark — for inside the AR mirror overlay) vs
 * "light" (default — for Settings + Routine page surfaces).
 */
const FaceShapeCalibrator = ({
  open,
  onClose,
  onSaved,
  currentShape = null,
  theme = "light",
}) => {
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState(currentShape);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchFaceShapeCatalog();
        if (!cancelled) setCatalog(data?.shapes || []);
      } catch {
        if (!cancelled)
          toast.error("Couldn't load the face-shape options.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    setSelected(currentShape);
  }, [currentShape, open]);

  const onSave = async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      const res = await saveFaceShape(selected, "user_set");
      track("face_shape_calibrated", { shape: selected });
      toast.success("Locked in — your contour now matches.");
      onSaved?.(res);
      onClose?.();
    } catch {
      toast.error("Couldn't save. Try again in a sec.");
    } finally {
      setSaving(false);
    }
  };

  const isDark = theme === "mirror";

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className={`fixed inset-0 z-[70] flex items-center justify-center px-4 py-8 overflow-y-auto ${
          isDark ? "bg-black/75" : "bg-[#2C2123]/55"
        } backdrop-blur-sm`}
        data-testid="face-shape-calibrator-root"
        onClick={(e) => {
          if (e.target === e.currentTarget && !saving) onClose?.();
        }}
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 10 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className={`relative max-w-2xl w-full rounded-3xl shadow-2xl ${
            isDark
              ? "bg-[#1a1416] border border-white/10 text-[#FAF6F2]"
              : "bg-white border border-[#EADDD7] text-[#2C2123]"
          }`}
        >
          <div className="p-7 sm:p-8">
            <div className="flex items-start gap-3 mb-5">
              <div
                className={`shrink-0 h-11 w-11 rounded-full flex items-center justify-center ${
                  isDark ? "bg-[#D5A8A4]/20 border border-[#D5A8A4]/40" : "bg-[#F2E8E9]"
                }`}
              >
                <Sparkles
                  className={`h-5 w-5 ${isDark ? "text-[#FFE6D7]" : "text-[#9B6A6C]"}`}
                />
              </div>
              <div className="flex-1">
                <div
                  className={`text-[11px] uppercase tracking-[0.22em] mb-1 ${
                    isDark ? "text-[#D5A8A4]" : "text-[#9B6A6C]"
                  }`}
                >
                  Calibrate your face shape
                </div>
                <h3
                  className={`text-xl sm:text-2xl font-light leading-tight ${
                    isDark ? "text-white" : "text-[#2C2123]"
                  }`}
                >
                  Which feels most like your face?
                </h3>
                <p
                  className={`text-sm mt-1.5 leading-relaxed max-w-lg ${
                    isDark ? "text-white/75" : "text-[#5C4B51]"
                  }`}
                >
                  We'll stop auto-detecting and tune your contour to the
                  shape you pick. You can switch back any time.
                </p>
              </div>
              <button
                type="button"
                onClick={() => !saving && onClose?.()}
                aria-label="Close calibrator"
                className={`shrink-0 h-8 w-8 rounded-full transition-colors flex items-center justify-center ${
                  isDark
                    ? "text-white/70 hover:text-white hover:bg-white/10"
                    : "text-[#8A7D82] hover:text-[#2C2123] hover:bg-[#F2E8E9]"
                }`}
                data-testid="face-shape-calibrator-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-6">
              {catalog.map((s) => {
                const on = selected === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSelected(s.id)}
                    className={`text-left rounded-2xl border px-4 py-3.5 transition-all ${
                      on
                        ? isDark
                          ? "bg-[#D5A8A4]/15 border-[#D5A8A4] text-white"
                          : "bg-[#9B6A6C]/10 border-[#9B6A6C] text-[#5D4050]"
                        : isDark
                          ? "bg-white/5 border-white/15 text-white/85 hover:border-[#D5A8A4]/50"
                          : "bg-white border-[#EADDD7] text-[#2C2123] hover:border-[#9B6A6C]/50"
                    }`}
                    aria-pressed={on}
                    data-testid={`face-shape-option-${s.id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-0.5">
                      <span className="font-medium capitalize text-sm">
                        {s.label}
                      </span>
                      {on && (
                        <Check
                          className={`h-4 w-4 shrink-0 mt-0.5 ${
                            isDark ? "text-[#FFE6D7]" : "text-[#9B6A6C]"
                          }`}
                        />
                      )}
                    </div>
                    <p
                      className={`text-xs leading-snug ${
                        isDark ? "text-white/60" : "text-[#8A7D82]"
                      }`}
                    >
                      {s.headline}
                    </p>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div
                className={`text-xs ${isDark ? "text-white/55" : "text-[#8A7D82]"}`}
              >
                {selected
                  ? "Ready to save."
                  : "Pick one — there's no wrong answer."}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => !saving && onClose?.()}
                  className={`rounded-full px-5 py-2.5 text-sm transition-colors ${
                    isDark
                      ? "text-white/80 hover:text-white hover:bg-white/10"
                      : "text-[#5C4B51] hover:text-[#2C2123] hover:bg-[#F2E8E9]"
                  }`}
                  data-testid="face-shape-calibrator-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onSave}
                  disabled={!selected || saving}
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDark
                      ? "bg-[#D5A8A4] hover:bg-[#E2BDB9] text-[#2C2123]"
                      : "bg-[#9B6A6C] hover:bg-[#7d5455] text-white"
                  }`}
                  data-testid="face-shape-calibrator-save"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save my shape
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FaceShapeCalibrator;
